import moment from 'moment';

import ShiftMapper from '@src/models/mappers/ShiftMapper';
import {
  SHIFT_ELASTIC_ENTITY,
  SHIFT_LIST_SEARCH_IDS_SIZE,
  SHIFT_TYPES,
} from '@src/packages/shifts/constants';
import { buildFullRecurrence } from '@src/packages/shifts/screens/utils';
import safeExecute from '@src/util/safeExecute';

import Shift from '../models/Shift';
import ShiftRepository from '../repositories/ShiftRepository';

class ShiftService {
  static WEEK_COUNT_CUSTOM = 1;
  static WEEK_COUNT = 4;
  static WEEK_COUNT_BIWEEKLY = 2;

  static MAX_WEEKLY_HOURS = 40;
  static MAX_DAYS = 6;

  /** Fixed shift */
  static MAX_FIXED_HOURS = 45;
  static MAX_FIXED_DAILY_HOURS = 10;

  /** Part time shift */
  static MAX_WEEKLY_HOURS_PART_TIME = 30;
  static MAX_PART_TIME_DAILY_HOURS = 6;

  /** Workday shift */
  static MAX_WORKDAY_DAILY_HOURS = 10;

  /** Biweekly shift */
  static MAX_BIWEEKLY_HOURS = 90;
  static MAX_BIWEEKLY_DAILY_HOURS = 5;
  static MAX_BIWEEKLY_DAILY_HOURS_WARNING = 10;

  constructor() {
    this.repository = new ShiftRepository();
    this.shift = new Shift();
  }

  /** * Helper functions ** */
  static formatMinutesToHHMM(totalMinutes) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  /**
   * Calculates the estimated weekly hours based on selected days and recurrence
   * @param {Array} days - Array of selected days
   * @param {Object} recurrence - Recurrence object containing startTime, endTime, and breakTime
   * @returns {{ minutes: number, hours: number, formated: string }}
   * @description
   * This function takes in an array of selected days and a recurrence object
   * and calculates the estimated weekly hours. It does this by subtracting
   * the start time from the end time, adding up the time for each day,
   * and then subtracting the break time. The result is the estimated
   * weekly hours.
   */

  static calculateTotalShiftHours(days, recurrence) {
    if (!days?.length || !recurrence?.startTime || !recurrence?.endTime) {
      return {
        minutes: 0,
        hours: 0,
        formated: '00:00',
      };
    }

    const { endTime, startTime, breakTime = 0 } = recurrence;

    if (!endTime || !startTime) {
      return {
        minutes: 0,
        hours: 0,
        formated: '00:00',
      };
    }

    // Calculate the difference in minutes between startTime and endTime
    const workMinutesPerDay = endTime.diff(startTime, 'minutes');

    // Calculate the total work time per week (minutes worked per day * number of days)
    const totalWorkMinutes = workMinutesPerDay * days.length;

    // Calculate the total break time per week
    const totalBreakTime = breakTime * days.length; // Total break time in minutes

    // Calculate the net work time (subtracting breaks)
    const netWorkMinutes = totalWorkMinutes - totalBreakTime;

    // Convert to hours with 2 decimal places
    const workHours = parseFloat((netWorkMinutes / 60).toFixed(2));

    // Format to HH:MM
    const formatedTime = this.formatMinutesToHHMM(netWorkMinutes);

    return {
      minutes: netWorkMinutes,
      hours: workHours,
      formated: formatedTime,
    };
  }

  static calculateTotalShiftHoursDynamicDays({ days = {} }) {
    const totalMinutes = Object.values(days).reduce((acc, data) => {
      const { startTime, endTime, breakTime, isActive } = data;

      if (!isActive) return acc;

      const workMinutesPerDay = endTime.diff(startTime, 'minutes');
      const totalWorkMinutes = workMinutesPerDay;
      const totalBreakTime = breakTime;
      const netWorkMinutes = totalWorkMinutes - totalBreakTime;

      return acc + netWorkMinutes;
    }, 0);

    return {
      minutes: totalMinutes,
      hours: parseFloat((totalMinutes / 60).toFixed(2)),
      formated: this.formatMinutesToHHMM(totalMinutes),
    };
  }

  // Helper function to convert recurrence days
  static parseActiveDays(days) {
    return Object.keys(days).reduce((acc, day) => {
      const dayData = days[day];
      if (dayData.isActive) {
        acc[day] = {
          isActive: dayData.isActive,
          breakTime: dayData.breakTime,
          startTime: dayData.startTime ? moment(dayData.startTime).format('HH:mm') : null,
          endTime: dayData.endTime ? moment(dayData.endTime).format('HH:mm') : null,
          startBreakTime: dayData.startBreakTime
            ? moment(dayData.startBreakTime).format('HH:mm')
            : null,
        };
      }
      return acc;
    }, {});
  }

  static convertRecurrenceDays(recurrence) {
    if (!recurrence?.weeks) return recurrence;

    let totalShiftHours = 0;
    const convertedWeeks = recurrence.weeks.map(week => {
      const weekHours = this.calculateTotalShiftHoursDynamicDays(week);
      totalShiftHours += weekHours.minutes;

      return {
        ...week,
        totalHours: weekHours.minutes,
        days: this.parseActiveDays(week.days),
      };
    });

    return {
      totalShiftHours,
      weeks: convertedWeeks,
    };
  }

  // Check every day into week to validate if break time is required
  static validateBreakTime(recurrence) {
    if (!recurrence?.weeks?.length) return false;

    const hasError = recurrence.weeks.some(week =>
      Object.values(week.days).some(
        day => day?.breakTime && day.breakTime > 0 && !day?.startBreakTime && day.isActive,
      ),
    );

    return hasError;
  }

  static buildResolution(resolution) {
    if (!resolution?.name && !resolution?.date) return null;

    return {
      name: resolution?.name || '',
      ...(resolution?.date ? { date: resolution.date.utc().toDate() } : {}),
    };
  }

  /** * PROCESS ** */

  /** Cycle shift */
  async createShiftCycle(shiftData) {
    try {
      const hasError = ShiftService.validateBreakTime(shiftData.recurrence);
      if (hasError) {
        throw new Error('Break time is required');
      }
      const preparedData = {
        ...shiftData,
        recurrence: ShiftService.convertRecurrenceDays(shiftData.recurrence),
        type: SHIFT_TYPES.CYCLE,
      };

      this.shift = new Shift(
        null,
        preparedData.weekCount,
        preparedData.days,
        preparedData.recurrence,
        preparedData.timeZone,
        preparedData.name,
        preparedData.regulationName,
        preparedData.type,
        shiftData.organizationId,
        shiftData.status,
      );

      const result = await this.repository.create(this.shift, shiftData.organizationId);

      // Create resolution if provided
      const resolutionData = ShiftService.buildResolution(shiftData?.resolution);
      if (resolutionData && result?.id) {
        await this.repository.createResolution(result.id, shiftData.organizationId, resolutionData);
      }

      return result;
    } catch (error) {
      throw new Error(`Service error creating shift: ${error.message}`);
    }
  }

  async updateShiftCycle(shiftData) {
    try {
      const hasError = ShiftService.validateBreakTime(shiftData.recurrence);
      if (hasError) {
        throw new Error('Break time is required');
      }

      const preparedData = {
        ...shiftData,
        recurrence: ShiftService.convertRecurrenceDays(shiftData.recurrence),
        type: SHIFT_TYPES.CYCLE,
      };

      this.shift = new Shift(
        shiftData.id,
        preparedData.weekCount,
        preparedData.days,
        preparedData.recurrence,
        preparedData.timeZone,
        preparedData.name,
        preparedData.regulationName,
        preparedData.type,
        shiftData.organizationId,
        shiftData.status,
      );

      const result = await this.repository.update(this.shift.toUpdate(), shiftData.organizationId);

      const resolutionData = ShiftService.buildResolution(shiftData?.resolution);
      if (resolutionData) {
        await this.repository.deleteResolutions(shiftData.id, shiftData.organizationId);
        await this.repository.createResolution(
          shiftData.id,
          shiftData.organizationId,
          resolutionData,
        );
      } else {
        await this.repository.deleteResolutions(shiftData.id, shiftData.organizationId);
      }

      return result;
    } catch (error) {
      throw new Error(`Service error updating shift: ${error.message}`);
    }
  }

  /** Biweekly shift */
  async createBiweeklyShift(shiftData) {
    try {
      const hasError = ShiftService.validateBreakTime(shiftData.recurrence);
      if (hasError) {
        throw new Error('Break time is required');
      }
      const preparedData = {
        ...shiftData,
        recurrence: ShiftService.convertRecurrenceDays(shiftData.recurrence),
        type: SHIFT_TYPES.BIWEEKLY,
      };

      this.shift = new Shift(
        null,
        ShiftService.WEEK_COUNT_BIWEEKLY,
        preparedData.days,
        preparedData.recurrence,
        preparedData.timeZone,
        preparedData.name,
        preparedData.regulationName,
        preparedData.type,
        shiftData.organizationId,
        shiftData.status,
      );

      const result = await this.repository.create(this.shift, shiftData.organizationId);

      // Create resolution if provided
      const resolutionData = ShiftService.buildResolution(shiftData?.resolution);
      if (resolutionData && result?.id) {
        await this.repository.createResolution(result.id, shiftData.organizationId, resolutionData);
      }

      return result;
    } catch (error) {
      throw new Error(`Service error creating shift: ${error.message}`);
    }
  }

  async updateBiweeklyShift(shiftData) {
    try {
      const hasError = ShiftService.validateBreakTime(shiftData.recurrence);
      if (hasError) {
        throw new Error('Break time is required');
      }
      const preparedData = {
        ...shiftData,
        recurrence: ShiftService.convertRecurrenceDays(shiftData.recurrence),
        type: SHIFT_TYPES.BIWEEKLY,
      };

      this.shift = new Shift(
        shiftData.id,
        ShiftService.WEEK_COUNT_BIWEEKLY,
        preparedData.days,
        preparedData.recurrence,
        preparedData.timeZone,
        preparedData.name,
        preparedData.regulationName,
        preparedData.type,
        shiftData.organizationId,
        shiftData.status,
      );

      const result = await this.repository.update(this.shift.toUpdate(), shiftData.organizationId);

      const resolutionData = ShiftService.buildResolution(shiftData?.resolution);
      if (resolutionData) {
        await this.repository.deleteResolutions(shiftData.id, shiftData.organizationId);
        await this.repository.createResolution(
          shiftData.id,
          shiftData.organizationId,
          resolutionData,
        );
      } else {
        await this.repository.deleteResolutions(shiftData.id, shiftData.organizationId);
      }

      return result;
    } catch (error) {
      throw new Error(`Service error updating shift: ${error.message}`);
    }
  }
  /** Workday shift */
  async createWorkdayShift(shiftData) {
    const recurrence = { weeks: [] };

    try {
      // Build recurrence for shiftWork shift
      Array.from({ length: ShiftService.WEEK_COUNT }).forEach(() => {
        recurrence.weeks.push({ days: shiftData.daysData });
      });

      const hasError = ShiftService.validateBreakTime(recurrence);
      if (hasError) {
        throw new Error('Break time is required');
      }

      const preparedData = {
        ...shiftData,
        recurrence: buildFullRecurrence(shiftData.days, shiftData.daysData),
        type: SHIFT_TYPES.SHIFT_WORK,
      };

      this.shift = new Shift(
        null,
        ShiftService.WEEK_COUNT,
        preparedData.days,
        preparedData.recurrence,
        preparedData.timeZone,
        preparedData.name,
        preparedData.regulationName,
        preparedData.type,
        shiftData.organizationId,
        shiftData.status,
      );

      const result = await this.repository.create(this.shift, shiftData.organizationId);

      // Create resolution if provided
      const resolutionData = ShiftService.buildResolution(shiftData?.resolution);
      if (resolutionData && result?.id) {
        await this.repository.createResolution(result.id, shiftData.organizationId, resolutionData);
      }

      return result;
    } catch (error) {
      throw new Error(`Service error creating shift: ${error.message}`);
    }
  }

  async updateWorkdayShift(shiftData) {
    try {
      const preparedData = {
        ...shiftData,
        recurrence: buildFullRecurrence(shiftData.days, shiftData.daysData),
        type: SHIFT_TYPES.SHIFT_WORK,
      };

      this.shift = new Shift(
        preparedData.id,
        preparedData.weekCount,
        preparedData.days,
        preparedData.recurrence,
        preparedData.timeZone,
        preparedData.name,
        preparedData.regulationName,
        preparedData.type,
        shiftData.organizationId,
        shiftData.status,
      );

      const result = await this.repository.update(this.shift.toUpdate(), shiftData.organizationId);

      // Handle resolution update
      const resolutionData = ShiftService.buildResolution(shiftData?.resolution);
      if (resolutionData) {
        // Delete existing resolutions first
        await this.repository.deleteResolutions(shiftData.id, shiftData.organizationId);
        // Create new resolution
        await this.repository.createResolution(
          shiftData.id,
          shiftData.organizationId,
          resolutionData,
        );
      } else {
        // If no resolution data, delete existing resolutions
        await this.repository.deleteResolutions(shiftData.id, shiftData.organizationId);
      }

      return result;
    } catch (error) {
      throw new Error(`Service error updating shift: ${error.message}`);
    }
  }

  /** Fixed shift */
  async createFixedShift(shiftData) {
    try {
      const preparedData = {
        ...shiftData,
        recurrence: buildFullRecurrence(shiftData.days, shiftData.recurrence, SHIFT_TYPES.FIXED),
        type: SHIFT_TYPES.FIXED,
      };

      this.shift = new Shift(
        null,
        ShiftService.WEEK_COUNT_CUSTOM,
        preparedData.days,
        preparedData.recurrence,
        preparedData.timeZone,
        preparedData.name,
        preparedData.regulationName,
        preparedData.type,
        shiftData.organizationId,
        shiftData.status,
      );

      const result = await this.repository.create(this.shift, shiftData.organizationId);

      // Create resolution if provided
      const resolutionData = ShiftService.buildResolution(shiftData?.resolution);
      if (resolutionData && result?.id) {
        await this.repository.createResolution(result.id, shiftData.organizationId, resolutionData);
      }

      return result;
    } catch (error) {
      throw new Error(`Service error creating shift: ${error.message}`);
    }
  }

  async updateFixedShift(shiftData) {
    try {
      const preparedData = {
        ...shiftData,
        recurrence: buildFullRecurrence(shiftData.days, shiftData.recurrence, SHIFT_TYPES.FIXED),
        type: SHIFT_TYPES.FIXED,
      };

      this.shift = new Shift(
        preparedData.id,
        ShiftService.WEEK_COUNT_CUSTOM,
        preparedData.days,
        preparedData.recurrence,
        preparedData.timeZone,
        preparedData.name,
        preparedData.regulationName,
        preparedData.type,
        shiftData.organizationId,
        shiftData.status,
      );

      const result = await this.repository.update(this.shift.toUpdate(), shiftData.organizationId);

      // Handle resolution update
      const resolutionData = ShiftService.buildResolution(shiftData?.resolution);
      if (resolutionData) {
        // Delete existing resolutions first
        await this.repository.deleteResolutions(shiftData.id, shiftData.organizationId);
        // Create new resolution
        await this.repository.createResolution(
          shiftData.id,
          shiftData.organizationId,
          resolutionData,
        );
      } else {
        // If no resolution data, delete existing resolutions
        await this.repository.deleteResolutions(shiftData.id, shiftData.organizationId);
      }

      return result;
    } catch (error) {
      throw new Error(`Service error updating shift: ${error.message}`);
    }
  }

  /** Part time shift */
  async createPartTimeShift(shiftData) {
    try {
      const preparedData = {
        ...shiftData,
        recurrence: buildFullRecurrence(
          shiftData.days,
          shiftData.recurrence,
          SHIFT_TYPES.PART_TIME,
        ),
        type: SHIFT_TYPES.PART_TIME,
      };

      this.shift = new Shift(
        null,
        ShiftService.WEEK_COUNT_CUSTOM,
        preparedData.days,
        preparedData.recurrence,
        preparedData.timeZone,
        preparedData.name,
        preparedData.regulationName,
        preparedData.type,
        shiftData.organizationId,
        shiftData.status,
      );

      const result = await this.repository.create(this.shift, shiftData.organizationId);

      // Create resolution if provided
      const resolutionData = ShiftService.buildResolution(shiftData?.resolution);
      if (resolutionData && result?.id) {
        await this.repository.createResolution(result.id, shiftData.organizationId, resolutionData);
      }

      return result;
    } catch (error) {
      throw new Error(`Service error creating shift: ${error.message}`);
    }
  }

  async updatePartTimeShift(shiftData) {
    try {
      const preparedData = {
        ...shiftData,
        recurrence: buildFullRecurrence(
          shiftData.days,
          shiftData.recurrence,
          SHIFT_TYPES.PART_TIME,
        ),
        type: SHIFT_TYPES.PART_TIME,
      };

      this.shift = new Shift(
        preparedData.id,
        ShiftService.WEEK_COUNT_CUSTOM,
        preparedData.days,
        preparedData.recurrence,
        preparedData.timeZone,
        preparedData.name,
        preparedData.regulationName,
        preparedData.type,
        shiftData.organizationId,
        shiftData.status,
      );

      const result = await this.repository.update(this.shift.toUpdate(), shiftData.organizationId);

      // Handle resolution update
      const resolutionData = ShiftService.buildResolution(shiftData?.resolution);
      if (resolutionData) {
        // Delete existing resolutions first
        await this.repository.deleteResolutions(shiftData.id, shiftData.organizationId);
        // Create new resolution
        await this.repository.createResolution(
          shiftData.id,
          shiftData.organizationId,
          resolutionData,
        );
      } else {
        // If no resolution data, delete existing resolutions
        await this.repository.deleteResolutions(shiftData.id, shiftData.organizationId);
      }
      return result;
    } catch (error) {
      throw new Error(`Service error updating shift: ${error.message}`);
    }
  }

  /** Delete shift */
  async deleteShift(shiftId, organizationId) {
    try {
      await this.repository.delete(shiftId, organizationId);
    } catch (error) {
      throw new Error(`Service error deleting shift: ${error.message}`);
    }
  }

  async getShift(shiftId, organizationId) {
    try {
      const shift = await this.repository.get(shiftId, organizationId);
      return shift;
    } catch (error) {
      throw new Error(`Service error getting shift: ${error.message}`);
    }
  }

  async getWorkJourneysByShiftId(shiftId, organizationId) {
    try {
      const workJourneys = await this.repository.getWorkJourneysByShiftId(shiftId, organizationId);
      return workJourneys;
    } catch (error) {
      throw new Error(`Service error getting work journeys by shift id: ${error.message}`);
    }
  }

  /**
   * Resolves work shift document ids matching list text search in Elastic.
   * Used as the ID source for Firestore `documentId in` realtime queries.
   */
  async searchIds({
    orgId,
    searchTerm = '',
    pageSize = SHIFT_LIST_SEARCH_IDS_SIZE,
    from = 0,
    exactMatch = false,
    shiftType = '',
    exceptional = null,
  }) {
    return safeExecute(
      async () => {
        if (!orgId) {
          throw new Error('Organization ID is required');
        }

        const filters = ShiftMapper.toListElasticFilters({
          searchTerm,
          exactMatch,
          shiftType,
          exceptional,
        });

        const params = {
          entity: SHIFT_ELASTIC_ENTITY,
          organizationId: orgId,
          filters,
          pagination: { from, size: pageSize },
          sort: [{ field: 'createdAt._seconds', order: 'desc' }],
          fields: ['id'],
        };

        const response = await this.repository.queryElastic(params);
        return ShiftMapper.fromElasticSearchIdsResponse(response);
      },
      'ShiftService.searchIds',
      { orgId, pageSize, from, shiftType, exceptional },
    );
  }
}

export default ShiftService;
